import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ShoppingCart,
  DollarSign,
  ClipboardList,
  Package,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  order_wise_kot_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import {
  handleError,
  inputdateformateChange,
} from "../CommonJquery/CommonJquery";

export default function KotSalesReport() {
  const { toast } = useToast();

  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [searchOrderId, setSearchOrderId] = useState("");

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const [stats, setStats] = useState({
    totalOrders: 0,
    totalKots: 0,
    totalQty: 0,
    totalRevenue: 0,
  });

  const formatNumber = (num) => {
    if (num === "-" || num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const getKotStatusLabel = (status) => {
    switch (parseInt(status)) {
      case 0:
        return "Pending";
      case 1:
        return "Preparing";
      case 2:
        return "Ready";
      case 3:
        return "Served";
      case 4:
        return "Cancelled";
      default:
        return status || "N/A";
    }
  };

  const getKotStatusStyle = (status) => {
    switch (parseInt(status)) {
      case 0:
        return "bg-warning/10 text-warning";
      case 1:
        return "bg-primary/10 text-primary";
      case 2:
        return "bg-success/10 text-success";
      case 3:
        return "bg-success/20 text-success";
      case 4:
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const fetchSalesData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);

      const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      fd.append("flag", flag);
      fd.append("call_id", call_id);
      fd.append("order_id", searchOrderId.trim());

      try {
        const response = await server_post_data(order_wise_kot_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const resData = response.data.data;
          const report = resData.report || [];

          setSalesData(report);
          setStats({
            totalOrders: resData.total_orders || 0,
            totalKots: resData.total_kots || 0,
            totalQty: resData.total_qty || 0,
            totalRevenue: resData.total_revenue || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching KOT data:", error);
        toast({
          title: "Error",
          description: "Failed to load KOT report data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, searchOrderId, toast],
  );

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchSalesData(startCal, endCal, "2", "0");
  }, [startCal, endCal, fetchSalesData]);

  function downloadExcel(data, filename = "KOT_Report.xlsx") {
    if (!data.length) {
      handleError("No data available for export!");
      return;
    }

    const formatted = data.map((item, index) => ({
      "Sr. No.": index + 1,
      "Order ID": item.order_id || "-",
      "Invoice ID": item.invoice_id || "-",
      "KOT Number": item.kot_number || "-",
      Date: inputdateformateChange(item.entry_date),
      "Guest Name": item.guest_name || "-",
      "Table Name": item.table_name || "-",
      "Assigned Staff": item.assign_staff_name || "-",
      Items: item.items || "-",
      "Total Qty": formatNumber(item.total_qty),
      "Total Amount (₹)": formatNumber(item.total_amount),
      "KOT Status": getKotStatusLabel(item.kot_status),
    }));

    const totalQty = data.reduce((s, r) => s + parseFloat(r.total_qty || 0), 0);
    const totalAmt = data.reduce(
      (s, r) => s + parseFloat(r.total_amount || 0),
      0,
    );

    formatted.push({
      "Sr. No.": "",
      "Order ID": "",
      "Invoice ID": "",
      "KOT Number": "",
      Date: "",
      "Guest Name": "",
      "Table Name": "",
      "Assigned Staff": "TOTAL",
      Items: "",
      "Total Qty": formatNumber(totalQty),
      "Total Amount (₹)": formatNumber(totalAmt),
      "KOT Status": "",
    });

    const sheet = XLSX.utils.json_to_sheet(formatted);
    sheet["!cols"] = [
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 14 },
      { wch: 15 },
      { wch: 18 },
      { wch: 15 },
      { wch: 18 },
      { wch: 40 },
      { wch: 12 },
      { wch: 16 },
      { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "KOT Report");
    XLSX.writeFile(wb, filename);
  }

  const totalPages = Math.ceil(salesData.length / entriesPerPage);
  const currentData = salesData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    fetchSalesData(startCal, endCal, "1", "0");
  }, []);

  return (
    <MainLayout>
      <Header
        title="KOT Report"
        subtitle="View and analyze KOT-wise order details"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalOrders}
                </p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalKots}
                </p>
                <p className="text-sm text-muted-foreground">Total KOTs</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatNumber(stats.totalQty)}
                </p>
                <p className="text-sm text-muted-foreground">Total Qty</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalRevenue)}
                </p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Order ID Search */}
              <div className="space-y-2">
                <Label>Order ID</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={searchOrderId}
                    onChange={(e) => setSearchOrderId(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Search Order ID..."
                    className="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  {searchOrderId && (
                    <button
                      onClick={() => setSearchOrderId("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Action Buttons */}
              <div
                className={cn(
                  "flex items-end gap-2",
                  filterOption !== "custom" && "md:col-span-2",
                )}
              >
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(salesData)}
                  className="gap-2"
                  disabled={salesData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Order ID
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Invoice ID
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    KOT Number
                  </TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Guest Name
                  </TableHead>
                  <TableHead className="text-muted-foreground">Table</TableHead>
                  <TableHead className="text-muted-foreground">Staff</TableHead>
                  <TableHead className="text-muted-foreground">Items</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Total Qty
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <TableRow key={index} className="border-border">
                      <TableCell>
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.order_id || "-"}
                      </TableCell>
                      <TableCell>{row.invoice_id || "-"}</TableCell>
                      <TableCell>{row.kot_number || "-"}</TableCell>
                      <TableCell>{formatDate(row.entry_date)}</TableCell>
                      <TableCell>{row.guest_name || "-"}</TableCell>
                      <TableCell>{row.table_name || "-"}</TableCell>
                      <TableCell>{row.assign_staff_name || "-"}</TableCell>
                      <TableCell
                        className="max-w-[200px] truncate"
                        title={row.items}
                      >
                        {row.items || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.total_qty)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatNumber(row.total_amount)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                            getKotStatusStyle(row.kot_status),
                          )}
                        >
                          {getKotStatusLabel(row.kot_status)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, salesData.length)} of{" "}
                {salesData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return (
                      <span key={pageNumber} className="px-1 flex items-center">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
